/**
 * Renders a typed name as a cursive PNG data URL sized to the given PDF bounding box.
 * The result is indistinguishable in layout from a drawn-signature image.
 */
function typedSignatureToDataUrl(name: string, widthMm: number, heightMm: number): string {
  // ~150 dpi for print-quality output
  const PX_PER_MM = 5.9;
  const W = Math.round(widthMm * PX_PER_MM);
  const H = Math.round(heightMm * PX_PER_MM);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#000000';

  // Prefer script/cursive fonts; italic serif is the reliable fallback
  const families = "'Brush Script MT', 'Segoe Script', cursive, Georgia";
  let fontSize = Math.round(H * 0.6);
  ctx.font = `italic ${fontSize}px ${families}`;

  while (fontSize > 10 && ctx.measureText(name).width > W * 0.9) {
    fontSize -= 2;
    ctx.font = `italic ${fontSize}px ${families}`;
  }

  const asc = ctx.measureText(name).actualBoundingBoxAscent ?? fontSize * 0.75;
  const textY = (H + asc) / 2;
  ctx.fillText(name, W * 0.05, textY);

  return canvas.toDataURL('image/png');
}

/**
 * Resolves a signature value to a PNG data URL for pdf.addImage().
 *
 * - Drawn signature: already a data URL — returned as-is.
 * - Typed signature ("TYPED:Name"): rendered onto a canvas sized to the
 *   PDF bounding box, returned as a data URL.
 * - Empty / invalid: returns null (caller skips addImage).
 */
export function resolveSignatureDataUrl(
  sig: string | undefined | null,
  widthMm: number,
  heightMm: number
): string | null {
  if (!sig) return null;

  if (sig.startsWith('TYPED:')) {
    const name = sig.slice(6).trim();
    return name ? typedSignatureToDataUrl(name, widthMm, heightMm) : null;
  }

  return sig.startsWith('data:') ? sig : null;
}
